import {
    EDIT_BID_SELECTION,
    RESET_BID_SELECTION,
    UPDATE_SELECTION_FILTERS,
} from './bidSelection.actions';
import { ScheduleSource, PeriodType, ShiftlineFilterSelectionTabs } from '../../Enums';

/** @type {EmployeeBidSelection.IBidSelectionStore} */
const initialState = {
    anchorDay: 0,
    dayCount: 0,
    employeeSelections: [],
    isDraft: false,
    filteredItems: [],
    items: [],
    paycodes: {},
    periodType: PeriodType.Weeks,
    rowHeight: 0,
    scheduleSource: -1,
    selectionFilters: {
        daySelectionItems: Array.from(Array(7).keys()),
        startDateHours: [0, 86400],
        isLocationIncludeTransfer: true,
        locationItems: [],
        isJobIncludeTransfer: true,
        jobItems: [],
        workplanItems: [],
        selectedTab: ShiftlineFilterSelectionTabs.Unselected,
        showTotalHours: false,
    },
    shifts: {},
};

/**
 * @param {EmployeeBidProfile.IGroupedScheduleTemplateItems[]} items
 * @param {BidProfile.ISchedulePatternsShiftTemplateDictionary} shifts
 * @param {{
 *  daySelectionItems: number[];
 *  startDateHours: number[];
 *  isLocationIncludeTransfer: boolean;
 *  locationItems: string[];
 *  isJobIncludeTransfer: boolean;
 *  jobItems: string[];
 *  workplanItems: string[];
 *  selectedTab: string;
 *  isCostCenterMode?: boolean;
 * }} filters
 * @param {ScheduleSource} scheduleSource
 */
export const filterItems = (items, shifts, filters, scheduleSource) => {
    const {
        daySelectionItems,
        startDateHours,
        isLocationIncludeTransfer,
        locationItems,
        isJobIncludeTransfer,
        jobItems,
        workplanItems,
        selectedTab,
        isCostCenterMode = false,
    } = filters;
    /**
     * @param {string} id
     * @returns {BidProfile.IShiftTemplate}
     */
    const getShiftTemplate = (id) => shifts[id];

    return items.filter((item) => {
        if (selectedTab === ShiftlineFilterSelectionTabs.Selected) {
            if (!item.isSelected) {
                return false;
            }
        }

        // In cost center mode, compare against fullOrgJobPath instead of primaryLocation
        const locationKey = isCostCenterMode ? item.fullOrgJobPath : item.primaryLocation;

        if (!locationItems.includes(locationKey)) {
            if (!isLocationIncludeTransfer) {
                return false;
            }

            if ((item.transferLocations.length === 0 || item.transferLocations.every((x) => !locationItems.includes(x)))) {
                return false;
            }
        }

        // Skip job filtering if in cost center mode
        if (!isCostCenterMode) {
            if (!jobItems.includes(item.primaryJob)) {
                if (!isJobIncludeTransfer) {
                    return false;
                }

                if (item.transferJobs.length === 0 || item.transferJobs.every((x) => !jobItems.includes(x))) {
                    return false;
                }
            }
        }

        if (!item.schedulePatternTemplate.items.every((x) => daySelectionItems.includes((x.day - 1) % 7))) {
            return false;
        }

        // eslint-disable-next-line max-len
        if (!item.schedulePatternTemplate.items.every((x) => (startDateHours[0] <= getShiftTemplate(x.shiftTemplate).startTime && startDateHours[1] >= getShiftTemplate(x.shiftTemplate).startTime))) {
            return false;
        }

        if (scheduleSource === ScheduleSource.WorkplanManager) {
            // @ts-ignore
            if (!workplanItems.includes(item.workplanName)) {
                return false;
            }
        }

        return true;
    });
};

/**
 * @param {EmployeeBidSelection.IBidSelectionStore} state
 * @param {ShiftBidding.IAction} action
 * */
// eslint-disable-next-line default-param-last
export default function employeeBidSelectionTemplateReducer(state = initialState, action) {
    const { type, payload } = action;
    switch (type) {
        case EDIT_BID_SELECTION: {
            const items = payload.items
                ? payload.items
                    .sort((a, b) => {
                        if (a.approvedRow) {
                            return -1;
                        }
                        if (b.approvedRow) {
                            return 1;
                        }
                        return a.position - b.position;
                    })
                : state.items;
            const shifts = payload.shifts ? payload.shifts : state.shifts;
            const scheduleSource = payload.scheduleSource ? payload.scheduleSource : state.scheduleSource;
            const filteredItems = filterItems(items, shifts, state.selectionFilters, scheduleSource);
            return {
                ...state,
                ...payload,
                filteredItems,
                items,
            };
        }
        case RESET_BID_SELECTION:
            return {
                ...initialState,
            };
        case UPDATE_SELECTION_FILTERS: {
            const filters = {
                ...state.selectionFilters,
                ...payload,
            };
            const filteredItems = filterItems(state.items, state.shifts, filters, state.scheduleSource);
            return {
                ...state,
                selectionFilters: filters,
                filteredItems,
            };
        }
        default:
            return state;
    }
}
